/**
 * Created: 2026-05-24
 * Refactor: Split UserCouponsPage into a directory
 *
 * Dialog for issuing a coupon from a template to a user (or as unbound).
 */

import { useMutation } from '@apollo/client/react'
import {
  currency,
  Field,
  type ISchema,
  message,
  NumberField,
  useForm,
} from '@homemaster/jslib'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import NumberInput from '../../components/NumberInput'
import UserAvatar from '../../components/UserAvatar'
import type { UserSelectorUser } from '../../components/UserSelector'
import { IssueCouponDocument } from '../../gql/graphql'
import { COUPON_CATEGORY_LABEL, SPORT_LABEL } from '../../lib/domainLabels'
import type { CouponTemplate } from './types'

interface IssueCouponDialogProps {
  user: UserSelectorUser | null
  templates: CouponTemplate[]
  onClose: () => void
  onIssued: () => void
}

interface IssueFormValues {
  templateId: string
  validDays: number | null
  quantity: number | null
}

const issueSchema: ISchema = {
  rules: {
    templateId: 'required',
    validDays: ['min:1', 'max:364'],
    quantity: ['required', 'min:1', 'max:10'],
  },
  messages: {
    'required.templateId': '请选择优惠券模板',
    'min.validDays': '有效天数须为正整数',
    'max.validDays': '有效天数不能超过364天',
    'required.quantity': '请输入数量',
    'min.quantity': '数量最少为1',
    'max.quantity': '数量最多为10',
  },
}

export default function IssueCouponDialog({
  user,
  templates,
  onClose,
  onIssued,
}: IssueCouponDialogProps) {
  const [issuing, setIssuing] = useState(false)
  const [issueCoupon] = useMutation(IssueCouponDocument)

  const form = useForm<IssueFormValues>(
    {
      templateId: templates[0]?.id ?? '',
      validDays: templates[0]?.validDays ?? null,
      quantity: 1,
    },
    issueSchema,
  )

  const selectedTemplate = templates.find((t) => t.id === form.values.templateId)

  const handleIssue = async () => {
    if (form.validate(false)) return
    setIssuing(true)
    try {
      const { data } = await issueCoupon({
        variables: {
          couponTemplateId: form.values.templateId,
          userId: user?.id ?? null,
          validDays: form.values.validDays ?? null,
          quantity: form.values.quantity ?? 1,
        },
      })
      const result = data?.issueCoupon
      if (result?.__typename === 'GraphqlRequestError') {
        message.error(result.message ?? '发放失败')
        return
      }
      message.success('优惠券已发放', { autoHide: 2000 })
      onIssued()
      onClose()
    } catch (err) {
      message.error(`发放失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setIssuing(false)
    }
  }

  return (
    <Dialog open onClose={onClose} disableRestoreFocus maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        发放优惠券
        {user ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CardGiftcardIcon fontSize="small" color="action" />
            <ArrowForwardIcon fontSize="small" color="action" />
            <UserAvatar user={user} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            未绑定优惠券
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={templates}
            value={selectedTemplate ?? undefined}
            onChange={(_, val) => {
              form.update({
                templateId: val?.id ?? '',
                validDays: val?.validDays ?? null,
              })
            }}
            getOptionLabel={(t) => t.name}
            renderOption={(props, t) => (
              <Box component="li" {...props} key={t.id}>
                <Box>
                  <Typography variant="body2">{t.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {COUPON_CATEGORY_LABEL[t.category]}
                    {t.discountAmount != null ? ` ${currency(t.discountAmount)}` : ''}
                    {t.sport ? ` · ${SPORT_LABEL[t.sport] ?? t.sport}` : ''}
                    {` · ${t.validDays}天`}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="优惠券模板"
                size="small"
                required
                variant="standard"
              />
            )}
            disableClearable
          />
          <Field
            form={form}
            name="validDays"
            label="有效天数"
            fullWidth
            slotProps={{
              input: {
                inputComponent: NumberInput,
                inputProps: { min: 1, max: 364, decimalScale: 0 },
              },
            }}
          />
          <Field
            form={form}
            name="quantity"
            label="数量"
            sx={{ width: 120 }}
            component={NumberField}
            componentProps={{
              inputProps: { min: 1, max: 10, decimalScale: 0 },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          variant="contained"
          onClick={handleIssue}
          disabled={!form.values.templateId || issuing}
        >
          确认发放
        </Button>
      </DialogActions>
    </Dialog>
  )
}
